import {
  Badge,
  Box,
  Button,
  Flex,
  Grid,
  Loader,
  LoadingOverlay,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useDebouncedState, useDisclosure, useHotkeys } from "@mantine/hooks";
import { IconCalendar, IconRefresh, IconSearch } from "@tabler/icons-react";
import dayjs from "dayjs";
import {
  MantineReactTable,
  MRT_ColumnDef,
  MRT_RowSelectionState,
  useMantineReactTable,
} from "mantine-react-table";
import React, { useDeferredValue, useEffect, useState } from "react";
import {
  keySearchBankingTransactions,
  repositoryPos,
} from "../../../_base/_const/_constVar";
import _breadcrumb from "../../../_base/component/_layout/_breadcrumb";
import { NotificationExtension } from "../../../_base/extension/NotificationExtension";
import { changeSearchValue } from "../../../_base/helper/FunctionHelper";
import { formatDateNotTimeZone } from "../../../common/FormatDate/FormatDate";
import { _localization } from "../../../config/location";
import { green } from "../../../const/variables";
import { MessageResponse } from "../../../model/MessageResponse";

const BankTransactions = () => {
  const headerRef = React.useRef<HTMLDivElement>(null);
  const [data, setData] = useState<any[]>([]);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [height, setHeight] = useState(0);
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 50,
  });
  const rowSelectionDeferred =
    useDeferredValue<MRT_RowSelectionState>(rowSelection);
  const [selectIds, setSelectIds] = useState<string[]>([]);
  const [rowSelect, setRowSelect] = useState<any[]>([]);
  const [visible, { toggle, open, close }] = useDisclosure(false);

  const _searchJson = localStorage.getItem(keySearchBankingTransactions)
    ? JSON.parse(localStorage.getItem(keySearchBankingTransactions) ?? "")
    : null;
  const checkedPinSearch = localStorage.getItem("checkedPinSearch") === "true";
  const [search, setSearch] = useState<any>({
    FromDate:
      _searchJson && checkedPinSearch
        ? _searchJson?.FromDate
        : new Date(
            new Date(
              new Date(new Date().setDate(new Date().getDate() - 9)).setHours(
                0,
                0,
                0,
                0,
              ),
            ).getTime() +
              7 * 60 * 60 * 1000,
          )?.toISOString(),
    ToDate:
      _searchJson && checkedPinSearch
        ? _searchJson?.ToDate
        : new Date(
            new Date(
              new Date(new Date().setDate(new Date().getDate() + 1)).setHours(
                0,
                0,
                0,
                0,
              ),
            ).getTime() +
              7 * 60 * 60 * 1000,
          )?.toISOString(),
  });
  const [keySearch, setKeySearch] = useDebouncedState<string>(
    _searchJson && checkedPinSearch ? _searchJson?.keySearch : "",
    300,
  );

  const columns = React.useMemo<MRT_ColumnDef<any>[]>(
    () => [
      {
        accessorKey: "ordernumber",
        header: "Mã đơn hàng",
        Cell: ({ renderedCellValue }) => (
          <Badge
            radius="sm"
            variant="dot"
            size="lg"
            color={renderedCellValue === null ? "red" : green.base}
          >
            {renderedCellValue}
          </Badge>
        ),
      },
      {
        accessorKey: "codetransaction",
        header: "Mã giao dịch",
        Cell: ({ renderedCellValue }) => (
          <Badge
            radius="sm"
            variant="dot"
            size="lg"
            color={renderedCellValue === null ? "red" : "aqua"}
          >
            {renderedCellValue}
          </Badge>
        ),
      },
      {
        accessorKey: "amountcallback",
        header: "Tiền giao dịch",
        Cell: ({ renderedCellValue }) => (
          <Text size="12.5px" fw={500}>
            {Number(renderedCellValue)?.toLocaleString()} đ
          </Text>
        ),
      },
      {
        accessorKey: "message",
        header: "Tin nhắn/Trạng thái",
      },
      {
        accessorKey: "createdate",
        header: "Thời gian tạo QR",
        Cell: ({ renderedCellValue }: any) => (
          <>
            {renderedCellValue &&
              dayjs(renderedCellValue)
                .add(7, "hour")
                .format("DD/MM/YYYY HH:mm:ss")}
          </>
        ),
      },
      {
        accessorKey: "datecallback",
        header: "Thời gian tạo QR thành công",
        Cell: ({ renderedCellValue }: any) => (
          <>
            {renderedCellValue &&
              dayjs(renderedCellValue)
                .add(7, "hour")
                .format("DD/MM/YYYY HH:mm:ss")}
          </>
        ),
      },
    ],
    [],
  );

  useEffect(() => {
    //   startTransition(() => {
    const valuesList = Object.keys(rowSelectionDeferred);
    setSelectIds(valuesList);
    if (valuesList.length < 1) setSelectIds([]);
    else {
      const valuesList = Object.keys(rowSelectionDeferred);
      setSelectIds(valuesList);
    }
    //  });
  }, [rowSelectionDeferred]);

  const fetchData = async () => {
    setIsLoading(true);
    setIsRefetching(true);
    setIsError(false);
    try {
      let url = `/api/v1/CreateSellItem/get-list-payment-transaction?Skip=${
        pagination.pageIndex * pagination.pageSize
      }&Take=${pagination.pageSize}`;

      const params = new URLSearchParams();
      Object.entries(search).forEach(([key, value]) => {
        if (
          value &&
          ((typeof value === "string" && value.length > 0) ||
            (Array.isArray(value) && value.length > 0))
        ) {
          params.append(key, String(value));
        }
      });

      if (keySearch.length > 0) {
        url += `&KeySearch=${keySearch?.trim()}`;
      }

      url += `&${params.toString()}`;

      const res = await repositoryPos.get<MessageResponse<any>>(url);

      if (res && res.success) {
        const result = res.data;
        setData(result);
        setRowCount(res.totalCount);
      } else {
        setData([]);
      }
    } catch (error) {
      setData([]);
      setIsError(true);
      console.error(error);
      return;
    } finally {
      setIsLoading(false);
      setIsRefetching(false);
      localStorage.setItem(
        keySearchBankingTransactions,
        JSON.stringify({ ...search, keySearch: keySearch }),
      );
    }
  };

  const handleSyncAutoBCRetry = async () => {
    open();
    try {
      const res = await repositoryPos.post<MessageResponse<any>>(
        "/api/v1/CreateSellItem/sync-autoBC-retry",
        rowSelect
          .filter((i) => i.ordernumber && i.codetransaction)
          .map((i) => ({
            orderNumber: i.ordernumber,
            sessionCode: i.codetransaction,
            amount: Number(i.amountcallback),
            attr1: "",
            attr2: "",
            attr3: "",
            attr4: "",
            attr5: "",
          })),
      );

      if (res && res.success) {
        NotificationExtension.Success("Đồng bộ báo có thành công !");
        setTimeout(() => {
          fetchData();
        }, 1200);
      }
    } catch (error) {
      console.log(error);
    } finally {
      close();
    }
  };

  useEffect(() => {
    fetchData();
  }, [pagination]);

  useEffect(() => {
    if (
      selectIds &&
      selectIds.length > 0 &&
      selectIds.length >= rowSelect.length
    ) {
      const dataSelect = data.filter((i) => selectIds.includes(String(i.id)));
      setRowSelect(dataSelect);
    } else if (
      selectIds &&
      selectIds.length > 0 &&
      selectIds.length < rowSelect.length
    ) {
      const dataSelect = rowSelect.filter(
        (i) => !selectIds.includes(String(i.id)),
      );
      setRowSelect(dataSelect);
    } else if (!selectIds || selectIds.length === 0) {
      setRowSelect([]);
    }
  }, [selectIds]);

  useEffect(() => {
    const headerHeight = headerRef.current?.offsetHeight || 0;
    const handleResize = () => {
      setHeight(window.innerHeight - (150 + headerHeight));
    };

    handleResize(); // Set initial height
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const table = useMantineReactTable({
    columns,
    data,
    enableColumnResizing: false,
    positionToolbarAlertBanner: "bottom",
    enableRowNumbers: true,
    //add custom action buttons to top-left of top toolbar
    enableTopToolbar: false,
    mantineTopToolbarProps: {
      style: {
        borderBottom: "3px solid rgba(128, 128, 128, 0.5)",
        marginBottom: 5,
      },
    },
    localization: _localization,
    enableRowSelection: true,
    getRowId: (row) => row.id?.toString(),
    initialState: {
      showColumnFilters: false,
      columnPinning: {
        // left: ["mrt-row-numbers", "ordernumber", "codetransaction"],
        // right: ["action"],
      },
      columnVisibility: { id: true, phone: false, fax: false },
      density: "xs",
    },
    mantineTableContainerProps: {
      style: { maxHeight: height, minHeight: height },
    },
    enableStickyHeader: true,
    manualPagination: true,
    onRowSelectionChange: setRowSelection,
    rowCount: rowCount,
    onPaginationChange: setPagination,
    mantineTableBodyCellProps: ({ row }) => ({
      style: {
        fontWeight: "500", //conditional styling
        fontSize: "12.5px",
        padding: "5px 15px",
      },
    }),
    state: {
      isLoading,
      pagination: pagination,
      showAlertBanner: isError,
      showProgressBars: isRefetching,
      showSkeletons: isLoading, //loading for the first time with no data
      rowSelection,
    },
    mantineToolbarAlertBannerProps: isError
      ? { color: "red", children: "Lỗi tải dữ liệu !" }
      : undefined,
    mantinePaginationProps: {
      showRowsPerPage: true,
      withEdges: true,
      rowsPerPageOptions: ["50", "100", "200"],
    },
    paginationDisplayMode: "pages",
    enableColumnPinning: true,
    mantineTableProps: {
      striped: false,
    },
    columnFilterDisplayMode: "popover",
    mantineTableBodyRowProps: ({ row }) => ({
      style: { cursor: "pointer" },
    }),
  });

  useHotkeys([
    [
      "Enter",
      () => {
        fetchData();
      },
    ],
  ]);

  return (
    <>
      <div
        ref={headerRef}
        style={{
          width: "100%",
        }}
      >
        <LoadingOverlay
          visible={visible}
          zIndex={1000}
          overlayProps={{ radius: "sm", blur: 5 }}
          loaderProps={{
            children: (
              <Flex direction={"column"} align="center" gap="xs">
                <Loader type="dots" size={35}></Loader>
                {/* <Title size={15}> Đang upload tài liệu...</Title> */}
                <Title size={15}> Đang xử lý....</Title>
              </Flex>
            ),
          }}
        />
        <Flex justify={"space-between"} align={"center"} w={"100%"}>
          <_breadcrumb></_breadcrumb>
          <Button
            leftSection={<IconRefresh size={16} />}
            disabled={
              !rowSelect ||
              rowSelect.length === 0 ||
              selectIds.length !== rowSelect.length
            }
            onClick={() => handleSyncAutoBCRetry()}
          >
            Đồng bộ báo có
          </Button>
        </Flex>
        <Grid mt={10} w={"100%"}>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <TextInput
              placeholder="Nhập từ khóa"
              defaultValue={keySearch}
              onChange={(e) => setKeySearch(e.currentTarget.value)}
              leftSection={<IconSearch color="#15aabf" />}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  fetchData();
                }
              }}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <DatePickerInput
              size="sm"
              placeholder="Chọn ngày bắt đầu"
              locale="vi"
              valueFormat="DD/MM/YYYY"
              value={
                search.FromDate && search.FromDate?.length > 0
                  ? new Date(search.FromDate ?? "")
                  : null
              }
              maxDate={
                search.ToDate && search.ToDate?.length > 0
                  ? new Date(search.ToDate)
                  : new Date()
              }
              onChange={(e) => {
                changeSearchValue(
                  e ? formatDateNotTimeZone(e ?? "") : "",
                  "FromDate",
                  setSearch,
                );
              }}
              leftSection={<IconCalendar color="#15aabf" />}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  fetchData();
                }
              }}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <DatePickerInput
              size="sm"
              placeholder="Chọn ngày kết thúc"
              locale="vi"
              valueFormat="DD/MM/YYYY"
              value={
                search.ToDate && search.ToDate?.length > 0
                  ? new Date(search.ToDate ?? "")
                  : null
              }
              minDate={
                search.FromDate && search.FromDate?.length > 0
                  ? new Date(search.FromDate)
                  : undefined
              }
              onChange={(e) => {
                changeSearchValue(
                  e ? formatDateNotTimeZone(e ?? "") : "",
                  "ToDate",
                  setSearch,
                );
              }}
              leftSection={<IconCalendar color="#15aabf" />}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  fetchData();
                }
              }}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <Button
              onClick={() => fetchData()}
              leftSection={<IconSearch size={14} />}
            >
              Tìm kiếm
            </Button>
          </Grid.Col>
        </Grid>
      </div>
      <Box mt={10}>
        <MantineReactTable table={table} />
      </Box>
    </>
  );
};

export default BankTransactions;
